import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Mail,
  Send,
  Eye,
  Users,
  Building2,
  Truck,
  Package,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  MessageCircle,
  ExternalLink,
  Plus,
  CalendarClock,
  Trash2,
  Pencil,
  Briefcase,
  UserPlus,
  Sparkles,
  Rocket,
  Settings,
  Save,
  FileText,
  BarChart3,
  Palette,
  Type,
  FlaskConical,
  Zap,
  Wrench,
  ChevronDown,
  ChevronRight,
  Building2 as Building2Icon,
  Paperclip
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CrmContactPicker from "@/components/admin/CrmContactPicker";
import EmailScheduler from "@/components/admin/EmailScheduler";
import ScheduleSendDialog from "@/components/admin/ScheduleSendDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// ===========================
// SECTION & CATEGORY CONFIGS
// ===========================

const SECTION_CONFIGS = {
  announcements: {
    title: "Platform Announcements",
    description: "Milestone announcements to healthcare partners — new platform features, domain migration, anniversaries, and major service upgrades",
    icon: Sparkles,
    borderColor: "border-cyan-300",
    bgGradient: "bg-gradient-to-r from-cyan-50 via-white to-amber-50",
    titleColor: "text-cyan-900",
    buttonColor: "bg-cyan-600 hover:bg-cyan-700",
  },
  outreach: {
    title: "Outreach Campaigns",
    description: "Send personalized outreach letters to investors, hospitals, suppliers/contractors, and prospects",
    icon: Briefcase,
    borderColor: "border-amber-200",
    bgGradient: "bg-gradient-to-r from-amber-50 to-orange-50",
    titleColor: "text-amber-900",
    buttonColor: "bg-amber-600 hover:bg-amber-700",
  },
  sterilemate: {
    title: "Sterile Mate Outreach Campaigns",
    description: "Specialized outreach letters for EVS/Janitorial, Sterile Processing, Water Purification, and General Contracting",
    icon: Sparkles,
    borderColor: "border-emerald-200",
    bgGradient: "bg-gradient-to-r from-emerald-50 to-teal-50",
    titleColor: "text-emerald-900",
    buttonColor: "bg-emerald-600 hover:bg-emerald-700",
  },
  ablabs: {
    title: "A&B Labs Partnership Campaigns",
    description: "Microbiology & Environmental Testing Services - Following LBJ Hospital In-Service",
    icon: FlaskConical,
    borderColor: "border-purple-200",
    bgGradient: "bg-gradient-to-r from-purple-50 to-indigo-50",
    titleColor: "text-purple-900",
    buttonColor: "bg-purple-600 hover:bg-purple-700",
  },
  sba: {
    title: "SBA Loan & Property Acquisition",
    description: "Formal letters for SBA lenders and property sellers - 20121 Highway 59 N acquisition",
    icon: Building2,
    borderColor: "border-blue-200",
    bgGradient: "bg-gradient-to-r from-blue-50 to-slate-50",
    titleColor: "text-blue-900",
    buttonColor: "bg-blue-600 hover:bg-blue-700",
  },
  supply_alerts: {
    title: "Supply Chain Alerts",
    description: "Backordered-item secondary sourcing estimates, allocation alerts, and hard-to-find supply notifications to hospital procurement teams",
    icon: AlertTriangle,
    borderColor: "border-red-200",
    bgGradient: "bg-gradient-to-r from-red-50 to-orange-50",
    titleColor: "text-red-900",
    buttonColor: "bg-red-600 hover:bg-red-700",
  },
  networking: {
    title: "Networking & Events",
    description: "Same-day follow-up letters from conferences, chamber events, HMSDC Category Catalyst, and business networking events",
    icon: Users,
    borderColor: "border-cyan-200",
    bgGradient: "bg-gradient-to-r from-cyan-50 to-sky-50",
    titleColor: "text-cyan-900",
    buttonColor: "bg-cyan-600 hover:bg-cyan-700",
  },
  outbound_partnerships: {
    title: "Outbound Partnership Proposals",
    description: "Personal, one-to-one letters accompanying formal Sterile Mate / Bleu Flux distribution & partnership proposals (Living Waters, brand-manufacturer outreach)",
    icon: Briefcase,
    borderColor: "border-fuchsia-200",
    bgGradient: "bg-gradient-to-r from-fuchsia-50 to-pink-50",
    titleColor: "text-fuchsia-900",
    buttonColor: "bg-fuchsia-600 hover:bg-fuchsia-700",
  },
  hospital_furniture: {
    title: "Healthcare Furniture Campaigns",
    description: "Brewer dental, Ritter/Midmark exam & procedure tables, and Millennium Series clinical stools — 21 configured models. Made in USA · TAA compliant · GSA schedule available.",
    icon: Building2,
    borderColor: "border-indigo-200",
    bgGradient: "bg-gradient-to-r from-indigo-50 to-blue-50",
    titleColor: "text-indigo-900",
    buttonColor: "bg-indigo-600 hover:bg-indigo-700",
  },
};

const SUBCATEGORY_LABELS = {
  evs: { label: "EVS / Janitorial (Filmop USA & CleanRead ATP)", dotColor: "bg-emerald-500", cardBorder: "border-emerald-200", titleColor: "text-emerald-800" },
  sterile_processing: { label: "Sterile Processing (SPD / CSSD)", dotColor: "bg-violet-500", cardBorder: "border-violet-200", titleColor: "text-violet-800" },
  water: { label: "Water Purification (TRITTON IXC / AAMI ST108)", dotColor: "bg-cyan-500", cardBorder: "border-cyan-200", titleColor: "text-cyan-800" },
  surgical: { label: "Surgical Product Line (Aspen Medical & BluBox)", dotColor: "bg-amber-500", cardBorder: "border-amber-200", titleColor: "text-amber-800" },
  american_surgical: { label: "American Surgical (Authorized Distributor — Salem, MA)", dotColor: "bg-rose-500", cardBorder: "border-rose-200", titleColor: "text-rose-800" },
  ent: { label: "ENT Product Line (BEAR-ENT — Ear, Nose & Throat)", dotColor: "bg-purple-500", cardBorder: "border-purple-200", titleColor: "text-purple-800" },
  higher_education: { label: "Higher Education (Colleges & Universities)", dotColor: "bg-indigo-500", cardBorder: "border-indigo-200", titleColor: "text-indigo-800" },
  general_contracting: { label: "General Contracting (Electrical, Plumbing & Construction)", dotColor: "bg-blue-500", cardBorder: "border-blue-200", titleColor: "text-blue-800" },
  facilities_management: { label: "Facilities Management (Janitorial & Water Purification)", dotColor: "bg-teal-500", cardBorder: "border-teal-200", titleColor: "text-teal-800" },
  lender: { label: "SBA Lender / Bank", dotColor: "bg-blue-600", cardBorder: "border-blue-200", titleColor: "text-blue-800" },
  seller: { label: "Property Seller / Owner", dotColor: "bg-slate-500", cardBorder: "border-slate-200", titleColor: "text-slate-800" },
  backorder_sourcing: { label: "Backordered Item — Secondary Sourcing Estimates", dotColor: "bg-red-500", cardBorder: "border-red-200", titleColor: "text-red-800" },
  june_2026_backorder_series: { label: "June 2026 Backorder Campaign (4-Part Series)", dotColor: "bg-amber-500", cardBorder: "border-amber-200", titleColor: "text-amber-800" },
  water_4week_series: { label: "Water Purification — Monthly 4-Week Series", dotColor: "bg-cyan-500", cardBorder: "border-cyan-200", titleColor: "text-cyan-800" },
  ablabs_4week_series: { label: "A&B Labs Microbiology — Monthly 4-Week Series", dotColor: "bg-purple-500", cardBorder: "border-purple-200", titleColor: "text-purple-800" },
  evs_4week_series: { label: "EVS / Janitorial (Filmop + Green Klean®) — Monthly 4-Week Series", dotColor: "bg-emerald-500", cardBorder: "border-emerald-200", titleColor: "text-emerald-800" },
  gc_4week_series: { label: "General Contracting — Monthly 4-Week Series", dotColor: "bg-blue-500", cardBorder: "border-blue-200", titleColor: "text-blue-800" },
  biomed_4week_series: { label: "Biomed — Monthly 4-Week Series (Water · Microbiology · Bioburden)", dotColor: "bg-emerald-500", cardBorder: "border-emerald-200", titleColor: "text-emerald-800" },
  diversity_4week_series: { label: "Supplier Diversity (MBE + SDVOSB) — Monthly 4-Week Series", dotColor: "bg-indigo-500", cardBorder: "border-indigo-200", titleColor: "text-indigo-800" },
  furniture_august_series: { label: "Healthcare Furniture — August 2026 Intro + 3-Week Drip (Brewer · Ritter · Millennium)", dotColor: "bg-indigo-500", cardBorder: "border-indigo-200", titleColor: "text-indigo-800" },
  team_internal: { label: "Internal Team Communications (Confidential — Do Not Forward)", dotColor: "bg-sky-500", cardBorder: "border-sky-200", titleColor: "text-sky-800" },
  availability_alerts: { label: "Product Availability Alerts (Time-Sensitive Sourcing)", dotColor: "bg-red-500", cardBorder: "border-red-200", titleColor: "text-red-800" },
  backorders_series: { label: "Backorders — Ongoing Series (American Surgical · Surgical · ENT · Sol Millennium)", dotColor: "bg-red-500", cardBorder: "border-red-200", titleColor: "text-red-800" },
  holiday_greeting: { label: "Holiday & Patriotic Greetings 🇺🇸", dotColor: "bg-red-500", cardBorder: "border-red-200", titleColor: "text-red-800" },
};

const CATEGORY_DROPDOWN_LABELS = {
  announcements: { label: "Platform Announcements", icon: Sparkles, color: "text-cyan-600" },
  telegram: { label: "Telegram Features", icon: MessageCircle, color: "text-blue-500" },
  welcome: { label: "Welcome Letters", icon: Mail, color: "text-green-500" },
  outreach: { label: "Outreach & Marketing", icon: Briefcase, color: "text-amber-500" },
  sterilemate: { label: "Sterile Mate Outreach", icon: Building2, color: "text-teal-500" },
  ablabs: { label: "A&B Labs Partnership", icon: FlaskConical, color: "text-purple-500" },
  sba: { label: "SBA Loan & Property", icon: Building2, color: "text-blue-600" },
  supply_alerts: { label: "Supply Chain Alerts", icon: AlertTriangle, color: "text-red-600" },
  networking: { label: "Networking & Events", icon: Users, color: "text-cyan-600" },
  outbound_partnerships: { label: "Outbound Partnerships", icon: Briefcase, color: "text-fuchsia-600" },
  hospital_furniture: { label: "Healthcare Furniture", icon: Building2, color: "text-indigo-600" },
};

// Template styles for AI generation
const TEMPLATE_STYLES = {
  professional: { name: "Professional", description: "Clean, hospital-grade, dark blue accents", icon: FileText },
  urgent: { name: "Urgent", description: "High-contrast, red 'Action Required' headers", icon: Zap },
  newsletter: { name: "Newsletter", description: "Multi-section, collaborative feel", icon: Mail },
  minimal: { name: "Minimal", description: "Text-based, direct 'from the CEO' style", icon: Type }
};

const AUDIENCE_OPTIONS = [
  { value: "all", label: "All Users", icon: Users },
  { value: "hospitals", label: "Hospital Staff", icon: Building2 },
  { value: "contractors", label: "Contractors", icon: Wrench },
  { value: "suppliers", label: "Suppliers", icon: Package }
];

// ===========================
// REUSABLE TEMPLATE CARD
// ===========================

function TemplateCard({ template, onPreview, onSend, onEdit, onAiRefresh, onDelete, buttonColor, onSchedule }) {
  return (
    <div className="p-4 bg-white rounded-lg border border-slate-200" data-testid={`template-card-${template.id}`}>
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="space-y-1 flex-1 min-w-0">
          <p className="font-medium text-slate-900">{template.name}</p>
          <p className="text-sm text-slate-600 truncate">
            <strong>Subject:</strong> {template.subject}
          </p>
          {template.description && (
            <p className="text-xs text-slate-400">{template.description}</p>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPreview(template.id, template.name)}
            data-testid={`preview-${template.id}-btn`}
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(template)}
              className="text-blue-700 border-blue-200 hover:bg-blue-50"
              data-testid={`edit-${template.id}-btn`}
              title="Edit subject, name, or full HTML"
            >
              <Pencil className="w-4 h-4 mr-1" /> Edit
            </Button>
          )}
          {onAiRefresh && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAiRefresh(template)}
              className="text-violet-700 border-violet-200 hover:bg-violet-50"
              data-testid={`ai-refresh-${template.id}-btn`}
              title="Use AI to give this letter a fresh visual + copy makeover"
            >
              <Sparkles className="w-4 h-4 mr-1" /> AI Refresh
            </Button>
          )}
          {onDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(template)}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid={`delete-${template.id}-btn`}
              title="Archive this template"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          {onSchedule && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSchedule(template)}
              className="text-violet-700 border-violet-300 hover:bg-violet-50"
              data-testid={`schedule-${template.id}-btn`}
              title="Schedule this email for a future date & time"
            >
              <CalendarClock className="w-4 h-4 mr-1" /> Schedule
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => onSend(template.id, template.subject)}
            className={buttonColor || "bg-slate-600 hover:bg-slate-700"}
            data-testid={`send-${template.id}-btn`}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}

// ===========================
// DYNAMIC TEMPLATE SECTION
// ===========================

function TemplateSection({ category, templates, onPreview, onSend, onSchedule, onEdit, onAiRefresh, onDelete, onBulkRefreshSeries, bulkRefreshingSubcat }) {
  const config = SECTION_CONFIGS[category];
  // Filter out archived templates from the main category flow so
  // last-month letters don't clutter the working view. Archived items
  // still exist in the manifest and can be sent — they simply hide from
  // the default admin dashboard.
  const categoryTemplates = templates.filter(t => t.category === category);

  // Group by subcategory
  const groups = {};
  categoryTemplates.forEach(t => {
    const key = t.subcategory || "_default";
    if (!groups[key]) groups[key] = [];
    groups[key].push(t);
  });

  // Collapsible state — older monthly series default collapsed so the admin
  // dashboard doesn't drown in 16 letters per category. Newest series and
  // ungrouped letters stay expanded by default. Stored per-category.
  const subcatEntries = Object.entries(groups);
  const seriesKeys = subcatEntries.filter(([k]) => k.endsWith("_series")).map(([k]) => k);
  // A series is "current" (expanded by default) if it has at least one
  // non-archived letter. Series that hold only archived (older-month)
  // letters collapse by default so the admin dashboard stays clean.
  const [collapsed, setCollapsed] = useState(() => {
    const initial = {};
    for (const [k, items] of subcatEntries) {
      if (!k.endsWith("_series")) continue;
      const hasCurrent = items.some(t => !t.archived);
      if (!hasCurrent) initial[k] = true;
    }
    return initial;
  });

  // Per-subcategory toggle for the "Archived from previous months"
  // accordion inside each series group.
  const [showArchived, setShowArchived] = useState({});

  // Hooks must come before any early-return — guard render below.
  if (!config) return null;
  if (categoryTemplates.length === 0) return null;

  const toggle = (k) => setCollapsed((c) => ({ ...c, [k]: !c[k] }));
  const collapseAll = () => {
    const next = {};
    for (const [k] of subcatEntries) next[k] = true;
    setCollapsed(next);
  };
  const expandAll = () => setCollapsed({});
  const archivableCount = seriesKeys.length;

  const Icon = config.icon;

  return (
    <Card className={`border-2 ${config.borderColor} ${config.bgGradient}`} data-testid={`section-${category}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <CardTitle className={`flex items-center gap-2 ${config.titleColor}`}>
              <Icon className="w-5 h-5" />
              {config.title}
            </CardTitle>
            <CardDescription>{config.description}</CardDescription>
          </div>
          {archivableCount > 1 && (
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={collapseAll}
                className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                data-testid={`section-${category}-collapse-all`}
                title="Collapse all monthly series — only headers stay visible"
              >
                Collapse all
              </button>
              <button
                type="button"
                onClick={expandAll}
                className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                data-testid={`section-${category}-expand-all`}
                title="Expand all monthly series"
              >
                Expand all
              </button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {subcatEntries.map(([subcat, items]) => {
          const subConfig = SUBCATEGORY_LABELS[subcat];
          const isSeries = subcat.endsWith("_series");
          const isRefreshing = bulkRefreshingSubcat === subcat;
          const isCollapsible = isSeries; // only series get collapsed
          const isCollapsed = !!collapsed[subcat];
          const currentCount = items.filter(t => !t.archived).length;
          const archivedCount = items.length - currentCount;
          return (
            <div key={subcat} className="space-y-3">
              {subConfig && (
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h4 className={`font-semibold ${subConfig.titleColor} flex items-center gap-2`}>
                    {isCollapsible ? (
                      <button
                        type="button"
                        onClick={() => toggle(subcat)}
                        className={`inline-flex items-center gap-2 hover:opacity-80`}
                        data-testid={`subcat-toggle-${subcat}`}
                        title={isCollapsed ? "Expand this series" : "Collapse this series"}
                      >
                        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        <span className={`w-2 h-2 ${subConfig.dotColor} rounded-full`}></span>
                        {subConfig.label}
                        {isCollapsed && (
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded">
                            {currentCount > 0 ? `${currentCount} current` : `${archivedCount} archived`}
                          </span>
                        )}
                      </button>
                    ) : (
                      <>
                        <span className={`w-2 h-2 ${subConfig.dotColor} rounded-full`}></span>
                        {subConfig.label}
                      </>
                    )}
                  </h4>
                  {isSeries && onBulkRefreshSeries && !isCollapsed && (
                    <button
                      type="button"
                      onClick={() => onBulkRefreshSeries(subcat, subConfig.label)}
                      disabled={isRefreshing}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${subConfig.cardBorder} ${subConfig.titleColor} bg-white hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1.5 shadow-sm`}
                      data-testid={`bulk-refresh-series-${subcat}`}
                      title="Use AI to refresh all letters in this monthly series with new content, layout & colors. Originals backed up."
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
                      {isRefreshing ? "Refreshing all letters…" : "Refresh All for Next Month"}
                    </button>
                  )}
                </div>
              )}
              {!isCollapsed && (() => {
                const current = items.filter(t => !t.archived);
                const archived = items.filter(t => t.archived);
                const isArchivedOpen = showArchived[subcat];
                return (
                  <>
                    {current.map(template => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        onPreview={onPreview}
                        onSend={onSend}
                        onSchedule={onSchedule}
                        onEdit={onEdit}
                        onAiRefresh={onAiRefresh}
                        onDelete={onDelete}
                        buttonColor={config.buttonColor}
                      />
                    ))}
                    {archived.length > 0 && (
                      <div className="mt-3 border-t border-slate-200 pt-3">
                        <button
                          type="button"
                          onClick={() => setShowArchived(prev => ({ ...prev, [subcat]: !isArchivedOpen }))}
                          className="w-full flex items-center justify-between gap-2 rounded-md bg-slate-50 hover:bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 transition-colors"
                          data-testid={`toggle-archived-${subcat}`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="text-slate-400">{isArchivedOpen ? "▾" : "▸"}</span>
                            <span>📁 Archived from previous months</span>
                            <span className="text-slate-400">({archived.length})</span>
                          </span>
                          <span className="text-[10px] uppercase tracking-widest text-slate-400">
                            {isArchivedOpen ? "Hide" : "Show"}
                          </span>
                        </button>
                        {isArchivedOpen && (
                          <div className="mt-3 space-y-3 opacity-75">
                            {archived.map(template => (
                              <TemplateCard
                                key={template.id}
                                template={template}
                                onPreview={onPreview}
                                onSend={onSend}
                                onSchedule={onSchedule}
                                onEdit={onEdit}
                                onAiRefresh={onAiRefresh}
                                onDelete={onDelete}
                                buttonColor={config.buttonColor}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ===========================
// MAIN COMPONENT
// ===========================

export default function EmailCampaigns() {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  // === Template Edit state ===
  const [editOpen, setEditOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null); // {id, name, subject, html, originalHtml, isAiRefreshed}
  const [savingEdit, setSavingEdit] = useState(false);
  const [aiRefreshing, setAiRefreshing] = useState(false);
  const [aiRefreshTemplateId, setAiRefreshTemplateId] = useState(null);
  const [editView, setEditView] = useState("preview"); // "preview" | "html" | "compare"
  
  // Send campaign state
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedRecipients, setSelectedRecipients] = useState("");
  const [subjectOverride, setSubjectOverride] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  
  // Custom recipients for outreach emails
  const [customRecipientsOpen, setCustomRecipientsOpen] = useState(false);
  const [customRecipients, setCustomRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState({ name: "", email: "", company: "" });
  const [selectedOutreachTemplate, setSelectedOutreachTemplate] = useState("");
  const [outreachSubject, setOutreachSubject] = useState("");

  // AI Campaign Composer state
  const [activeMainTab, setActiveMainTab] = useState("templates");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiStyle, setAiStyle] = useState("professional");
  const [aiAudience, setAiAudience] = useState("all");
  const [aiCustomInstructions, setAiCustomInstructions] = useState("");
  const [aiGeneratedSubject, setAiGeneratedSubject] = useState("");
  const [aiGeneratedBody, setAiGeneratedBody] = useState("");
  const [aiFullPreviewHtml, setAiFullPreviewHtml] = useState("");
  const [aiCampaignName, setAiCampaignName] = useState("");
  const [aiCampaigns, setAiCampaigns] = useState([]);
  const [audienceCounts, setAudienceCounts] = useState({});
  const [generating, setGenerating] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [showAiPreview, setShowAiPreview] = useState(false);
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [optimizeVersions, setOptimizeVersions] = useState([]);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [selectedAiCampaign, setSelectedAiCampaign] = useState(null);
  const [launchTestEmails, setLaunchTestEmails] = useState("");
  const [launchingCampaign, setLaunchingCampaign] = useState(false);
  const [showBrandingModal, setShowBrandingModal] = useState(false);
  const [branding, setBranding] = useState({});

  // Department-targeted send modal state
  const [deptSendOpen, setDeptSendOpen] = useState(false);
  const [deptSendTemplate, setDeptSendTemplate] = useState("");
  const [deptSendHealthSystems, setDeptSendHealthSystems] = useState([]); // selected ids
  const [deptSendDepartments, setDeptSendDepartments] = useState([]);    // selected dept names
  const [deptSendSubject, setDeptSendSubject] = useState("");
  // Named audience segment — "exec_finance" keeps CFO/finance/exec leadership
  // plus supply chain directors & VPs, and drops day-to-day procurement staff.
  const [deptSendSegment, setDeptSendSegment] = useState(null);
  const [deptPreview, setDeptPreview] = useState(null); // {total_recipients, by_department, by_health_system, recipients}
  const [deptSending, setDeptSending] = useState(false);
  const [healthSystemList, setHealthSystemList] = useState([]);

  useEffect(() => {
    fetchData();
    fetchAiCampaigns();
    fetchAudienceCounts();
    fetchBranding();
    fetchHealthSystemsForDept();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, logsRes] = await Promise.all([
        fetch(`${API}/email-templates`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/email-campaign/logs`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }
      if (logsRes.ok) {
        const data = await logsRes.json();
        setLogs(data.logs || []);
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
      toast.error("Failed to load email campaigns data");
    } finally {
      setLoading(false);
    }
  };

  const fetchAiCampaigns = async () => {
    try {
      const res = await fetch(`${API}/campaigns`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setAiCampaigns(data.campaigns || []);
      }
    } catch (err) {
      console.error("Failed to fetch AI campaigns:", err);
    }
  };

  const fetchAudienceCounts = async () => {
    try {
      const res = await fetch(`${API}/campaigns/audience/preview`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setAudienceCounts(data.audience_counts || {});
      }
    } catch (err) {
      console.error("Failed to fetch audience counts:", err);
    }
  };

  const fetchBranding = async () => {
    try {
      const res = await fetch(`${API}/campaigns/branding`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setBranding(data.branding || {});
      }
    } catch (err) {
      console.error("Failed to fetch branding:", err);
    }
  };

  const fetchHealthSystemsForDept = async () => {
    try {
      const res = await fetch(`${API}/health-systems`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setHealthSystemList(data.health_systems || []);
      }
    } catch (err) { console.error("Failed to fetch health systems for dept campaign:", err); }
  };

  const refreshDeptPreview = async (hsIds, depts, segment = deptSendSegment) => {
    if (!hsIds || hsIds.length === 0) { setDeptPreview(null); return; }
    try {
      const res = await fetch(`${API}/admin/email-campaign/preview-by-department`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          health_system_ids: hsIds,
          departments: depts && depts.length ? depts : null,
          segment: segment || null,
        })
      });
      if (res.ok) {
        setDeptPreview(await res.json());
      } else {
        setDeptPreview({ total_recipients: 0, by_department: {}, by_health_system: {}, recipients: [] });
      }
    } catch (err) { setDeptPreview(null); }
  };

  const sendDepartmentCampaign = async () => {
    if (!deptSendTemplate) { toast.error("Pick a template first"); return; }
    if (!deptSendHealthSystems.length) { toast.error("Pick at least one health system"); return; }
    if (!deptPreview || deptPreview.total_recipients === 0) {
      toast.error("No recipients match the current filter");
      return;
    }
    setDeptSending(true);
    try {
      const res = await fetch(`${API}/admin/email-campaign/send-by-department`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          template_id: deptSendTemplate,
          health_system_ids: deptSendHealthSystems,
          departments: deptSendDepartments.length ? deptSendDepartments : null,
          segment: deptSendSegment || null,
          subject_override: deptSendSubject || null,
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Sent to ${data.success_count}/${data.total_recipients} contacts`);
        setDeptSendOpen(false);
        setDeptSendTemplate("");
        setDeptSendHealthSystems([]);
        setDeptSendDepartments([]);
        setDeptSendSubject("");
        setDeptPreview(null);
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to send department campaign");
      }
    } catch (err) {
      toast.error("Failed to send department campaign");
    } finally {
      setDeptSending(false);
    }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) { toast.error("Please enter a prompt for the email content"); return; }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/campaigns/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: aiPrompt, style: aiStyle, target_audience: aiAudience, custom_instructions: aiCustomInstructions || undefined })
      });
      if (res.ok) {
        const data = await res.json();
        setAiGeneratedSubject(data.subject || "");
        setAiGeneratedBody(data.body_html || "");
        setAiFullPreviewHtml(data.full_html || "");
        toast.success("Email content generated with AI!");
      } else { const err = await res.json(); toast.error(err.detail || "Failed to generate content"); }
    } catch { toast.error("Network error. Please try again."); }
    finally { setGenerating(false); }
  };

  const handleAiOptimize = async () => {
    if (!aiGeneratedBody.trim()) { toast.error("Generate content first before optimizing"); return; }
    setOptimizing(true);
    try {
      const res = await fetch(`${API}/campaigns/optimize`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_html: aiGeneratedBody, focus: "persuasion" })
      });
      if (res.ok) { const data = await res.json(); setOptimizeVersions(data.versions || []); setShowOptimizeModal(true); }
      else { const err = await res.json(); toast.error(err.detail || "Failed to optimize content"); }
    } catch { toast.error("Network error. Please try again."); }
    finally { setOptimizing(false); }
  };

  const handleSaveAiCampaign = async () => {
    if (!aiCampaignName.trim() || !aiGeneratedSubject.trim() || !aiGeneratedBody.trim()) {
      toast.error("Please fill in campaign name and generate content first"); return;
    }
    setSavingCampaign(true);
    try {
      const res = await fetch(`${API}/campaigns`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: aiCampaignName, subject: aiGeneratedSubject, body_html: aiGeneratedBody, style: aiStyle, target_audience: aiAudience })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success("Campaign saved!");
        setSelectedAiCampaign(data.campaign);
        fetchAiCampaigns();
        setAiCampaignName("");
      } else { const err = await res.json(); toast.error(err.detail || "Failed to save campaign"); }
    } catch { toast.error("Network error. Please try again."); }
    finally { setSavingCampaign(false); }
  };

  const [pickedCrmContacts, setPickedCrmContacts] = useState([]);
  const [crmPickerOpen, setCrmPickerOpen] = useState(false);
  const [outreachPickerOpen, setOutreachPickerOpen] = useState(false);

  const handleLaunchAiCampaign = async (testMode = false) => {
    if (!selectedAiCampaign) { toast.error("Please select a campaign to launch"); return; }
    setLaunchingCampaign(true);
    try {
      const payload = {
        campaign_id: selectedAiCampaign.id,
        test_mode: testMode,
        test_emails: testMode ? launchTestEmails.split(",").map(e => e.trim()).filter(Boolean) : undefined,
      };
      // Explicit CRM-contact recipient list overrides the campaign's target_audience
      if (!testMode && pickedCrmContacts.length > 0) {
        payload.recipients = pickedCrmContacts.map((c) => ({
          email: c.email,
          name: c.name,
          account_id: c.account_id,
          contact_id: c.contact_id,
          department: c.department,
        }));
      }
      const res = await fetch(`${API}/campaigns/${selectedAiCampaign.id}/launch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Campaign launched!");
        setShowLaunchModal(false);
        setPickedCrmContacts([]);
        fetchAiCampaigns();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to launch campaign");
      }
    } catch { toast.error("Network error. Please try again."); }
    finally { setLaunchingCampaign(false); }
  };

  const handleUpdateBranding = async () => {
    try {
      const res = await fetch(`${API}/campaigns/branding`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(branding)
      });
      if (res.ok) { toast.success("Branding updated!"); setShowBrandingModal(false); }
      else { const err = await res.json(); toast.error(err.detail || "Failed to update branding"); }
    } catch { toast.error("Network error. Please try again."); }
  };

  const deleteAiCampaign = async (campaignId) => {
    try {
      const res = await fetch(`${API}/campaigns/${campaignId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success("Campaign deleted"); fetchAiCampaigns(); }
    } catch { toast.error("Failed to delete campaign"); }
  };

  const handlePreview = async (templateId, templateName) => {
    try {
      const res = await fetch(`${API}/email-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { const data = await res.json(); setPreviewHtml(data.html); setPreviewTitle(templateName); setPreviewOpen(true); }
      else { toast.error("Failed to load template preview"); }
    } catch { toast.error("Failed to load template"); }
  };

  // ===== Template Edit / AI Refresh / Delete handlers =======================
  const handleEditTemplate = async (template) => {
    try {
      const r = await fetch(`${API}/email-templates/${template.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Could not fetch template");
      // get raw HTML, not the preview-with-replacements one
      const rawRes = await fetch(`${API}/email-templates/${template.id}/preview`);
      const rawHtml = await rawRes.text();
      setEditingTemplate({
        ...template,
        html: rawHtml,
        originalHtml: rawHtml,
      });
      setEditOpen(true);
    } catch (e) {
      toast.error(e.message || "Failed to load template for editing");
    }
  };

  const handleAiRefreshTemplate = async (template) => {
    const instructions = window.prompt(
      `AI Refresh "${template.name}"\n\nThe AI will keep all images, links, prices, and merge variables — but freshen the visual style and copy.\n\nOptional direction (leave blank for a general refresh):`,
      ""
    );
    if (instructions === null) return; // user hit cancel
    setAiRefreshing(true);
    setAiRefreshTemplateId(template.id);
    toast.message("AI is refreshing your letter... (15-30s)", { duration: 8000 });
    try {
      const r = await fetch(`${API}/email-templates/${template.id}/ai-refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ instructions: instructions || "" }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "AI refresh failed");
      }
      const data = await r.json();
      // Open in editor, side-by-side w/ original
      const rawRes = await fetch(`${API}/email-templates/${template.id}/preview`);
      const originalHtml = await rawRes.text();
      setEditingTemplate({
        ...template,
        html: data.refreshed_html,
        originalHtml,
        isAiRefreshed: true,
      });
      setEditOpen(true);
      toast.success("AI refresh ready — review & save");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAiRefreshing(false);
      setAiRefreshTemplateId(null);
    }
  };

  const [bulkRefreshingSubcat, setBulkRefreshingSubcat] = useState(null);

  const handleBulkRefreshSeries = async (subcat, label) => {
    const confirmed = window.confirm(
      `Refresh ALL letters in "${label}"?\n\n` +
      "The AI will rewrite every letter in this monthly series with:\n" +
      "  • New body copy (same theme, same prices, same links)\n" +
      "  • A rotating color palette + layout\n" +
      "  • Fresh hero accents and section structure\n\n" +
      "Originals are backed up to /trash automatically. This may take 1-3 minutes."
    );
    if (!confirmed) return;
    setBulkRefreshingSubcat(subcat);
    toast.message(`Refreshing ${label}…`, { duration: 12000 });
    try {
      const r = await fetch(`${API}/email-templates/series/${subcat}/refresh-bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Bulk refresh failed");
      }
      const data = await r.json();
      const ok = data.succeeded ?? 0;
      const total = data.count ?? 0;
      if (ok === total) {
        toast.success(`Refreshed all ${ok} letters in this series — preview them to review.`);
      } else {
        toast.message(`Refreshed ${ok} of ${total}. Some letters failed — check logs.`);
      }
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBulkRefreshingSubcat(null);
    }
  };

  const handleDeleteTemplate = async (template) => {
    const confirmed = window.confirm(
      `Archive "${template.name}"?\n\nThe letter will be moved to a backup folder (recoverable by support if needed) and removed from this list.`
    );
    if (!confirmed) return;
    try {
      const r = await fetch(`${API}/email-templates/${template.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Delete failed");
      }
      toast.success(`${template.name} archived`);
      fetchData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const saveTemplateEdits = async () => {
    if (!editingTemplate) return;
    setSavingEdit(true);
    try {
      const r = await fetch(`${API}/email-templates/${editingTemplate.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          html: editingTemplate.html,
          subject: editingTemplate.subject,
          name: editingTemplate.name,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Save failed");
      }
      toast.success(`${editingTemplate.name} saved`);
      setEditOpen(false);
      setEditingTemplate(null);
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const fetchRecipientCount = async (recipientType) => {
    try {
      const res = await fetch(`${API}/admin/bulk-email/recipients-preview?recipient_type=${recipientType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { const data = await res.json(); setRecipientCount(data.total_count || 0); }
    } catch { console.error("Failed to get recipient count"); }
  };

  const handleRecipientChange = (value) => {
    setSelectedRecipients(value);
    if (value) fetchRecipientCount(value);
  };

  const handleSendCampaign = async () => {
    if (!selectedTemplate || !selectedRecipients) { toast.error("Please select a template and recipients"); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/admin/email-campaign/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ template_id: selectedTemplate, recipient_type: selectedRecipients, subject_override: subjectOverride || null })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Campaign sent! ${data.success_count}/${data.total_recipients} delivered`);
        setConfirmOpen(false); setSelectedTemplate(""); setSelectedRecipients(""); setSubjectOverride("");
        fetchData();
      } else { toast.error(data.detail || "Failed to send campaign"); }
    } catch { toast.error("Failed to send campaign"); }
    finally { setSending(false); }
  };

  const addCustomRecipient = () => {
    if (!newRecipient.name || !newRecipient.email) { toast.error("Name and email are required"); return; }
    if (!newRecipient.email.includes("@")) { toast.error("Please enter a valid email address"); return; }
    setCustomRecipients([...customRecipients, { ...newRecipient, id: Date.now() }]);
    setNewRecipient({ name: "", email: "", company: "" });
  };

  const removeCustomRecipient = (id) => {
    setCustomRecipients(customRecipients.filter(r => r.id !== id));
  };

  const handleSendOutreach = async () => {
    if (customRecipients.length === 0) { toast.error("Please add at least one recipient"); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/admin/email-campaign/send-custom`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          template_id: selectedOutreachTemplate,
          recipients: customRecipients.map(r => ({ email: r.email, name: r.name, company: r.company })),
          subject: outreachSubject
        })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Outreach email sent to ${data.success_count} recipient(s)!`);
        setCustomRecipientsOpen(false); setCustomRecipients([]);
        fetchData();
      } else { toast.error(data.detail || "Failed to send outreach email"); }
    } catch { toast.error("Failed to send outreach email"); }
    finally { setSending(false); }
  };

  // Handler for outreach send buttons
  const handleOutreachSend = (templateId, subject) => {
    setSelectedOutreachTemplate(templateId);
    setOutreachSubject(subject);
    setCustomRecipientsOpen(true);
  };

  // Schedule a template for a future send
  const [scheduleTemplate, setScheduleTemplate] = useState(null);
  const handleScheduleTemplate = (template) => {
    setScheduleTemplate(template);
  };

  // Group templates by category for the dropdown
  const templatesByCategory = useMemo(() => {
    const groups = {};
    templates.forEach(t => {
      if (!groups[t.category]) groups[t.category] = [];
      groups[t.category].push(t);
    });
    return groups;
  }, [templates]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="email-campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-blue-600" />
            Email Campaigns
          </h2>
          <p className="text-slate-500 mt-1">Send targeted emails and create AI-powered campaigns</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => setDeptSendOpen(true)}
            data-testid="open-dept-campaign-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            Send by Department
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowBrandingModal(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Branding
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email Templates
          </TabsTrigger>
          <TabsTrigger value="ai-composer" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            AI Campaign Composer
          </TabsTrigger>
          <TabsTrigger value="saved-campaigns" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Saved Campaigns ({aiCampaigns.length})
          </TabsTrigger>
          <TabsTrigger value="scheduler" className="flex items-center gap-2" data-testid="scheduler-tab">
            <CalendarClock className="w-4 h-4" />
            Schedule &amp; Automate
          </TabsTrigger>
        </TabsList>

        {/* ==============================
            TEMPLATES TAB - DYNAMIC
        ============================== */}
        <TabsContent value="templates" className="mt-6">
          {/* Send Campaign Card - Dropdown based */}
          <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <Send className="w-5 h-5" />
                Send Email Campaign
              </CardTitle>
              <CardDescription>Select a template and recipients to send a mass email</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Email Template</Label>
                  <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                    <SelectTrigger data-testid="template-select">
                      <SelectValue placeholder="Select template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(templatesByCategory).map(([cat, items]) => {
                        const cfg = CATEGORY_DROPDOWN_LABELS[cat] || { label: cat, icon: Mail, color: "text-slate-500" };
                        const CatIcon = cfg.icon;
                        return (
                          <div key={cat}>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 border-t first:border-t-0 mt-1 pt-2 first:mt-0 first:pt-1.5">
                              {cfg.label}
                            </div>
                            {items.map(t => (
                              <SelectItem key={t.id} value={t.id}>
                                <span className="flex items-center gap-2">
                                  <CatIcon className={`w-3 h-3 ${cfg.color}`} />
                                  {t.name}
                                </span>
                              </SelectItem>
                            ))}
                          </div>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Send To</Label>
                  <Select value={selectedRecipients} onValueChange={handleRecipientChange}>
                    <SelectTrigger data-testid="recipients-select">
                      <SelectValue placeholder="Select recipients..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hospitals"><span className="flex items-center gap-2"><Building2 className="w-3 h-3 text-blue-600" />All Hospitals</span></SelectItem>
                      <SelectItem value="contractors"><span className="flex items-center gap-2"><Truck className="w-3 h-3 text-green-600" />All Contractors</span></SelectItem>
                      <SelectItem value="suppliers"><span className="flex items-center gap-2"><Package className="w-3 h-3 text-orange-600" />All Suppliers</span></SelectItem>
                      <SelectItem value="all"><span className="flex items-center gap-2"><Users className="w-3 h-3 text-purple-600" />All Users</span></SelectItem>
                    </SelectContent>
                  </Select>
                  {recipientCount > 0 && <p className="text-xs text-slate-500">{recipientCount} recipients</p>}
                </div>
                
                <div className="space-y-2">
                  <Label>Subject Override (Optional)</Label>
                  <Input
                    placeholder="Custom subject line..."
                    value={subjectOverride}
                    onChange={(e) => setSubjectOverride(e.target.value)}
                    data-testid="subject-override-input"
                  />
                </div>
                
                <div className="flex items-end gap-2">
                  {selectedTemplate && (
                    <Button variant="outline" onClick={() => {
                      const template = templates.find(t => t.id === selectedTemplate);
                      if (template) handlePreview(template.id, template.name);
                    }}>
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                  )}
                  <Button onClick={() => setConfirmOpen(true)} disabled={!selectedTemplate || !selectedRecipients} className="bg-blue-600 hover:bg-blue-700" data-testid="send-campaign-btn">
                    <Send className="w-4 h-4 mr-2" />
                    Send Campaign
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ======= DYNAMIC SECTIONS (Outreach, Sterile Mate, Supply Alerts, A&B Labs, SBA) ======= */}
          {["announcements", "outreach", "sterilemate", "hospital_furniture", "supply_alerts", "outbound_partnerships", "networking", "ablabs", "sba"].map(category => (
            <TemplateSection
              key={category}
              category={category}
              templates={templates}
              onPreview={handlePreview}
              onSend={handleOutreachSend}
              onSchedule={handleScheduleTemplate}
              onEdit={handleEditTemplate}
              onAiRefresh={handleAiRefreshTemplate}
              onDelete={handleDeleteTemplate}
              onBulkRefreshSeries={handleBulkRefreshSeries}
              bulkRefreshingSubcat={bulkRefreshingSubcat}
            />
          ))}

          {/* Telegram Bulk Invites */}
          <TelegramBulkInvite token={token} onSendComplete={fetchData} />

          {/* Campaign History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-500" />
                Campaign History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No campaigns sent yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Template</TableHead>
                      <TableHead>Recipients</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Failed</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{log.template_id}</TableCell>
                        <TableCell>{log.recipient_type || "custom"}</TableCell>
                        <TableCell><Badge className="bg-green-100 text-green-700">{log.success_count}</Badge></TableCell>
                        <TableCell>{log.failed_count > 0 ? <Badge className="bg-red-100 text-red-700">{log.failed_count}</Badge> : <span className="text-slate-400">0</span>}</TableCell>
                        <TableCell className="text-slate-500 text-sm">{log.sent_at ? new Date(log.sent_at).toLocaleString() : "N/A"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==============================
            AI COMPOSER TAB
        ============================== */}
        <TabsContent value="ai-composer" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  AI Email Composer
                </CardTitle>
                <CardDescription>Describe your email and let AI generate professional content</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div><Label>What is the email about?</Label><Textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="e.g., Announce our new water purification service partnership..." className="mt-1 min-h-[100px]" data-testid="ai-prompt-input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Style</Label><Select value={aiStyle} onValueChange={setAiStyle}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(TEMPLATE_STYLES).map(([key, style]) => (<SelectItem key={key} value={key}><span className="flex items-center gap-2"><style.icon className="w-3 h-3" />{style.name}</span></SelectItem>))}</SelectContent></Select></div>
                  <div><Label>Audience</Label><Select value={aiAudience} onValueChange={setAiAudience}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{AUDIENCE_OPTIONS.map(opt => (<SelectItem key={opt.value} value={opt.value}><span className="flex items-center gap-2"><opt.icon className="w-3 h-3" />{opt.label}</span></SelectItem>))}</SelectContent></Select></div>
                </div>
                <div><Label>Custom Instructions (Optional)</Label><Input value={aiCustomInstructions} onChange={(e) => setAiCustomInstructions(e.target.value)} placeholder="e.g., Mention Harris Health case study..." className="mt-1" /></div>
                <Button onClick={handleAiGenerate} disabled={generating} className="w-full bg-gradient-to-r from-purple-600 to-indigo-600" data-testid="ai-generate-btn">{generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate Email</>}</Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Generated Content</CardTitle>
                <CardDescription>{aiGeneratedSubject ? "Review and save your AI-generated email" : "Content will appear here after generation"}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {aiGeneratedSubject ? (
                  <>
                    <div><Label>Subject Line</Label><Input value={aiGeneratedSubject} onChange={(e) => setAiGeneratedSubject(e.target.value)} className="mt-1 font-semibold" /></div>
                    <div className="border rounded-lg p-4 max-h-64 overflow-y-auto bg-slate-50"><div dangerouslySetInnerHTML={{ __html: aiGeneratedBody }} className="prose prose-sm max-w-none" /></div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setShowAiPreview(true)}><Eye className="w-4 h-4 mr-2" />Full Preview</Button>
                      <Button variant="outline" onClick={handleAiOptimize} disabled={optimizing}>{optimizing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}Optimize</Button>
                    </div>
                    <div className="border-t pt-4 space-y-3">
                      <div><Label>Campaign Name</Label><Input value={aiCampaignName} onChange={(e) => setAiCampaignName(e.target.value)} placeholder="e.g., Q1 Water Purification Outreach" className="mt-1" data-testid="ai-campaign-name" /></div>
                      <Button onClick={handleSaveAiCampaign} disabled={savingCampaign} className="w-full">{savingCampaign ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Campaign</>}</Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-400"><Sparkles className="w-12 h-12 mb-4 opacity-30" /><p>Enter a prompt and click &quot;Generate Email&quot;</p></div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ==============================
            SAVED CAMPAIGNS TAB
        ============================== */}
        <TabsContent value="saved-campaigns" className="mt-6">
          {aiCampaigns.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No saved campaigns yet. Use the AI Composer to create one.</p></CardContent></Card>
          ) : (
            <div className="space-y-4">
              {aiCampaigns.map(campaign => (
                <Card key={campaign.id} className="hover:border-blue-300 transition-colors">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800">{campaign.name}</p>
                      <p className="text-sm text-slate-500 truncate">{campaign.subject}</p>
                      <div className="flex gap-2 mt-1 flex-wrap items-center"><Badge variant="outline" className="text-xs">{campaign.style}</Badge><Badge variant="outline" className="text-xs">{campaign.target_audience}</Badge>{campaign.status && <Badge className={campaign.status === "sent" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}>{campaign.status}</Badge>}{(campaign.attachments || []).map((a) => (
                        <Badge key={a.path} className="bg-blue-50 text-blue-700 border border-blue-200 text-xs gap-1" data-testid={`campaign-attachment-${campaign.id}`}>
                          <Paperclip className="w-3 h-3" />{a.name}
                        </Badge>
                      ))}</div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button variant="outline" size="sm" onClick={() => { setSelectedAiCampaign(campaign); setShowLaunchModal(true); }}><Rocket className="w-4 h-4 mr-1" />Launch</Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteAiCampaign(campaign.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ==============================
            SCHEDULE & AUTOMATE TAB
        ============================== */}
        <TabsContent value="scheduler" className="mt-6">
          <EmailScheduler token={token} templates={templates} />
        </TabsContent>
      </Tabs>

      {/* ==============================
          ALL MODALS
      ============================== */}

      {/* Template Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader><DialogTitle>{previewTitle}</DialogTitle><DialogDescription>Template Preview</DialogDescription></DialogHeader>
          <div className="overflow-auto max-h-[70vh] border rounded-lg">
            <iframe srcDoc={previewHtml} title="Email Preview" className="w-full h-[600px] border-0" sandbox="allow-same-origin" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Edit / AI-Refresh Modal */}
      <Dialog open={editOpen} onOpenChange={(o) => { if (!o) { setEditOpen(false); setEditingTemplate(null); } }}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" />
              Edit Letter
              {editingTemplate?.isAiRefreshed && (
                <Badge className="bg-violet-100 text-violet-700 gap-1">
                  <Sparkles className="w-3 h-3" /> AI-Refreshed Draft
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Update the subject, name, or full HTML body. Changes are saved to the template file and version-backed up automatically.
            </DialogDescription>
          </DialogHeader>

          {editingTemplate && (
            <div className="flex-1 overflow-auto space-y-3 px-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Template name</Label>
                  <Input
                    value={editingTemplate.name || ""}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                    data-testid="edit-template-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Subject line</Label>
                  <Input
                    value={editingTemplate.subject || ""}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                    data-testid="edit-template-subject-input"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between border-b pb-2">
                <div className="flex gap-1">
                  {["preview", "html", editingTemplate.isAiRefreshed ? "compare" : null].filter(Boolean).map((view) => (
                    <button
                      key={view}
                      type="button"
                      onClick={() => setEditView(view)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                        editView === view
                          ? "bg-blue-600 text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                      data-testid={`edit-view-${view}`}
                    >
                      {view === "preview" && "Live Preview"}
                      {view === "html" && "Edit HTML"}
                      {view === "compare" && "Side-by-Side"}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  Images, links &amp; merge variables ({"{{first_name}}"}, etc.) work in all views.
                </p>
              </div>

              {editView === "preview" && (
                <iframe
                  srcDoc={editingTemplate.html}
                  title="Edit Preview"
                  className="w-full h-[500px] border rounded-lg"
                  sandbox="allow-same-origin"
                  data-testid="edit-live-preview"
                />
              )}
              {editView === "html" && (
                <Textarea
                  value={editingTemplate.html}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, html: e.target.value })}
                  className="font-mono text-xs h-[500px]"
                  data-testid="edit-html-textarea"
                />
              )}
              {editView === "compare" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">Original</p>
                    <iframe srcDoc={editingTemplate.originalHtml} title="Original" className="w-full h-[500px] border rounded-lg" sandbox="allow-same-origin" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-violet-700 uppercase mb-1 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> AI Refreshed
                    </p>
                    <iframe srcDoc={editingTemplate.html} title="AI Refreshed" className="w-full h-[500px] border-2 border-violet-300 rounded-lg" sandbox="allow-same-origin" />
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex justify-between sm:justify-between border-t pt-3">
            <div className="flex gap-2">
              {editingTemplate?.isAiRefreshed && (
                <Button
                  variant="outline"
                  onClick={() => setEditingTemplate({ ...editingTemplate, html: editingTemplate.originalHtml, isAiRefreshed: false })}
                  data-testid="edit-revert-ai"
                >
                  ↺ Revert to original
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setEditOpen(false); setEditingTemplate(null); }}>
                Cancel
              </Button>
              <Button
                onClick={saveTemplateEdits}
                disabled={savingEdit}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="edit-template-save"
              >
                {savingEdit ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Send Modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-blue-600" />Confirm Send Campaign</DialogTitle>
            <DialogDescription>You are about to send an email campaign. This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg"><span className="text-slate-600">Template:</span><span className="font-medium">{templates.find(t => t.id === selectedTemplate)?.name}</span></div>
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg"><span className="text-slate-600">Recipients:</span><span className="font-medium capitalize">{selectedRecipients} ({recipientCount} users)</span></div>
            {subjectOverride && <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg"><span className="text-slate-600">Subject:</span><span className="font-medium text-sm">{subjectOverride}</span></div>}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleSendCampaign} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-send-btn">
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send to {recipientCount} Users</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Recipients Dialog for Outreach Emails */}
      <Dialog open={customRecipientsOpen} onOpenChange={setCustomRecipientsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-amber-600" />Send Outreach Email</DialogTitle>
            <DialogDescription>
              <span className="block mb-2">Pick existing CRM contacts (or add a new lead inline), or type recipients manually below.</span>
              <span className="text-xs bg-slate-100 px-2 py-1 rounded"><strong>Subject:</strong> {outreachSubject}</span>
            </DialogDescription>
          </DialogHeader>

          {/* CRM pick row */}
          <div className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="text-sm">
              <p className="font-semibold text-amber-900 flex items-center gap-1.5">
                <Users className="w-4 h-4" /> Pick from CRM
              </p>
              <p className="text-xs text-amber-800/80">Filter by department, multi-select, or click "Add new lead" inside the picker.</p>
            </div>
            <Button
              onClick={() => setOutreachPickerOpen(true)}
              className="bg-amber-600 hover:bg-amber-700 gap-1.5"
              data-testid="outreach-open-crm-picker"
            >
              <Users className="w-4 h-4" /> Pick from CRM…
            </Button>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h4 className="font-medium mb-3 flex items-center gap-2"><UserPlus className="w-4 h-4" />Or add manually</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label className="text-xs">Name *</Label><Input placeholder="John Smith" value={newRecipient.name} onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })} data-testid="investor-recipient-name" /></div>
              <div><Label className="text-xs">Email *</Label><Input type="email" placeholder="john@company.com" value={newRecipient.email} onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })} data-testid="investor-recipient-email" /></div>
              <div><Label className="text-xs">Company</Label><Input placeholder="ABC Ventures" value={newRecipient.company} onChange={(e) => setNewRecipient({ ...newRecipient, company: e.target.value })} data-testid="investor-recipient-company" /></div>
            </div>
            <Button onClick={addCustomRecipient} className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-base font-semibold" data-testid="add-investor-recipient-btn"><Plus className="w-5 h-5 mr-2" />ADD TO RECIPIENT LIST</Button>
          </div>
          {customRecipients.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium mb-2 text-sm flex items-center gap-2"><span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">Ready to Send</span>Recipients ({customRecipients.length})</h4>
              <div className="border border-green-200 rounded-lg divide-y max-h-60 overflow-y-auto bg-green-50">
                {customRecipients.map((r) => (
                  <div key={r.id} className="flex items-center justify-between p-3 hover:bg-green-100">
                    <div><p className="font-medium text-sm">{r.name}</p><p className="text-xs text-slate-500">{r.email} {r.company && `- ${r.company}`}</p></div>
                    <Button variant="ghost" size="sm" onClick={() => removeCustomRecipient(r.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {customRecipients.length === 0 && (
            <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200"><Users className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p className="font-medium">No recipients added yet</p><p className="text-sm text-slate-400">Fill in the form above and click &quot;ADD TO RECIPIENT LIST&quot;</p></div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCustomRecipientsOpen(false)}>Cancel</Button>
            <Button onClick={handleSendOutreach} disabled={customRecipients.length === 0 || sending} className={customRecipients.length > 0 ? "bg-green-600 hover:bg-green-700" : "bg-slate-400"} data-testid="send-outreach-confirm-btn">
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send to {customRecipients.length} Recipient{customRecipients.length !== 1 ? "s" : ""}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Preview Modal */}
      <Dialog open={showAiPreview} onOpenChange={setShowAiPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>AI Generated Email Preview</DialogTitle><DialogDescription>This is how your email will appear to recipients</DialogDescription></DialogHeader><div className="border rounded-lg overflow-hidden bg-slate-100 p-4"><div className="bg-white shadow-lg mx-auto" style={{ maxWidth: "600px" }} dangerouslySetInnerHTML={{ __html: aiFullPreviewHtml }} /></div></DialogContent>
      </Dialog>

      {/* AI Optimize Modal */}
      <Dialog open={showOptimizeModal} onOpenChange={setShowOptimizeModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-600" />AI-Optimized Versions</DialogTitle><DialogDescription>Choose a version to use</DialogDescription></DialogHeader>
          <div className="space-y-4">
            {optimizeVersions.map((version, idx) => (
              <Card key={idx} className="hover:border-blue-300 cursor-pointer" onClick={() => { setAiGeneratedBody(version.html); setShowOptimizeModal(false); toast.success(`Applied: ${version.name}`); }}>
                <CardHeader className="pb-2"><CardTitle className="text-sm">{version.name}</CardTitle></CardHeader>
                <CardContent><div className="text-sm text-slate-600 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: version.html }} /></CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Launch Campaign Modal */}
      <Dialog open={showLaunchModal} onOpenChange={setShowLaunchModal}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Rocket className="w-5 h-5 text-amber-500" />Launch Campaign</DialogTitle><DialogDescription>{selectedAiCampaign?.name} - {audienceCounts[selectedAiCampaign?.target_audience] || 0} default recipients</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4"><p className="text-sm text-amber-800"><strong>From:</strong> orders@sterilemate.com</p></div>

            {/* CRM Hospital Contacts override (Supply Chain, Janitorial, etc) */}
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-700">Send to specific Hospital Contacts (optional)</Label>
                <Button size="sm" variant="outline" onClick={() => setCrmPickerOpen(true)} className="gap-1.5" data-testid="open-crm-picker">
                  <Users className="w-3.5 h-3.5" /> Pick from CRM…
                </Button>
              </div>
              {pickedCrmContacts.length === 0 ? (
                <p className="text-xs text-slate-500 mt-2">
                  Leave empty to send to the campaign's default audience ({audienceCounts[selectedAiCampaign?.target_audience] || 0} users).
                  Or pick specific people from the CRM directory, filter by department (e.g. Supply Chain, Janitorial), and add new leads inline.
                </p>
              ) : (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-emerald-700">
                      {pickedCrmContacts.length} CRM contact{pickedCrmContacts.length === 1 ? "" : "s"} selected
                    </p>
                    <button onClick={() => setPickedCrmContacts([])} className="text-xs text-slate-500 hover:text-rose-600">Clear</button>
                  </div>
                  <div className="max-h-[140px] overflow-y-auto space-y-1 rounded bg-slate-50 border border-slate-200 p-2">
                    {pickedCrmContacts.slice(0, 12).map((c, i) => (
                      <div key={i} className="text-xs flex items-center justify-between">
                        <span className="truncate">
                          <strong>{c.name || c.email}</strong>{c.department ? ` · ${c.department}` : ""}
                        </span>
                        <span className="text-slate-400 ml-2 truncate">{c.account_name}</span>
                      </div>
                    ))}
                    {pickedCrmContacts.length > 12 && <p className="text-[10px] text-slate-400 mt-1">+ {pickedCrmContacts.length - 12} more…</p>}
                  </div>
                </div>
              )}
            </div>

            <div><Label>Test Mode (Optional)</Label><Input value={launchTestEmails} onChange={(e) => setLaunchTestEmails(e.target.value)} placeholder="test@example.com, another@example.com" className="mt-1" /><p className="text-xs text-slate-500 mt-1">Send a test first to make sure the email renders correctly</p></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLaunchModal(false)}>Cancel</Button>
            {launchTestEmails.trim() && <Button variant="outline" onClick={() => handleLaunchAiCampaign(true)} disabled={launchingCampaign}>{launchingCampaign ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Send Test</Button>}
            <Button onClick={() => handleLaunchAiCampaign(false)} disabled={launchingCampaign} className="bg-gradient-to-r from-amber-500 to-orange-500" data-testid="launch-campaign-final-btn">
              {launchingCampaign ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Rocket className="w-4 h-4 mr-2" />}
              {pickedCrmContacts.length > 0 ? `Launch to ${pickedCrmContacts.length} CRM contact${pickedCrmContacts.length === 1 ? "" : "s"}` : "Launch to default audience"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CRM contact picker — multi-select w/ add-lead inline */}
      <CrmContactPicker
        open={crmPickerOpen}
        onOpenChange={setCrmPickerOpen}
        mode="multi"
        onConfirm={(picked) => setPickedCrmContacts(picked)}
        confirmLabel="Use as campaign recipients"
        title="Pick campaign recipients from CRM"
      />

      {/* CRM picker for the Outreach (template Send) flow — appends into customRecipients */}
      <CrmContactPicker
        open={outreachPickerOpen}
        onOpenChange={setOutreachPickerOpen}
        mode="multi"
        title="Pick outreach recipients from CRM"
        confirmLabel="Add to recipient list"
        onConfirm={(picked) => {
          if (!picked || picked.length === 0) return;
          // De-dupe by email against existing list
          const existing = new Set(
            (customRecipients || []).map((r) => (r.email || "").toLowerCase())
          );
          const next = [...customRecipients];
          let added = 0;
          picked.forEach((c) => {
            const email = (c.email || "").toLowerCase();
            if (!email || existing.has(email)) return;
            existing.add(email);
            next.push({
              id: `${c.account_id || ""}-${email}-${Date.now()}-${added}`,
              name: c.name || c.account_name || email,
              email,
              company: c.account_name || "",
              account_id: c.account_id,
              contact_id: c.contact_id,
              department: c.department,
            });
            added += 1;
          });
          setCustomRecipients(next);
          if (added > 0) {
            toast.success(`Added ${added} CRM contact${added === 1 ? "" : "s"} to the recipient list`);
          } else {
            toast.info("Those contacts are already in the recipient list");
          }
        }}
      />

      {/* Branding Settings Modal */}
      <Dialog open={showBrandingModal} onOpenChange={setShowBrandingModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-blue-600" />Email Branding Settings</DialogTitle><DialogDescription>Configure global header and footer for AI campaigns</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div><Label>Header Image URL</Label><Input value={branding.header_image_url || ""} onChange={(e) => setBranding({ ...branding, header_image_url: e.target.value })} className="mt-1" /></div>
            <div><Label>Footer Logo URL</Label><Input value={branding.footer_logo_url || ""} onChange={(e) => setBranding({ ...branding, footer_logo_url: e.target.value })} className="mt-1" /></div>
            <div className="border-t pt-4"><Label className="text-slate-700 font-medium">Signature Block</Label></div>
            <div><Label>Name</Label><Input value={branding.owner_name || ""} onChange={(e) => setBranding({ ...branding, owner_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Title</Label><Input value={branding.owner_title || ""} onChange={(e) => setBranding({ ...branding, owner_title: e.target.value })} className="mt-1" /></div>
            <div><Label>Company Details</Label><Input value={branding.company_details || ""} onChange={(e) => setBranding({ ...branding, company_details: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Phone</Label><Input value={branding.contact_phone || ""} onChange={(e) => setBranding({ ...branding, contact_phone: e.target.value })} className="mt-1" /></div>
              <div><Label>Email</Label><Input value={branding.contact_email || ""} onChange={(e) => setBranding({ ...branding, contact_email: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Website</Label><Input value={branding.website || ""} onChange={(e) => setBranding({ ...branding, website: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowBrandingModal(false)}>Cancel</Button><Button onClick={handleUpdateBranding}><Save className="w-4 h-4 mr-2" />Save Branding</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Department-Targeted Campaign Send Modal */}
      <Dialog open={deptSendOpen} onOpenChange={(open) => { setDeptSendOpen(open); if (!open) { setDeptPreview(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dept-campaign-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Send Campaign by Department
            </DialogTitle>
            <DialogDescription>
              Pick a template, choose one or more healthcare systems, then filter by department to email the contacts you want — Supply Chain, EVS, Bio Med, etc.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Step 1: Template */}
            <div>
              <Label className="text-sm font-semibold text-slate-700">1. Template</Label>
              <Select value={deptSendTemplate} onValueChange={setDeptSendTemplate}>
                <SelectTrigger className="mt-1.5" data-testid="dept-template-select">
                  <SelectValue placeholder="Select an email template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} <span className="text-xs text-slate-400 ml-2">· {t.category}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {deptSendTemplate && (
                <Input
                  className="mt-2 text-sm"
                  placeholder="Optional subject override..."
                  value={deptSendSubject}
                  onChange={(e) => setDeptSendSubject(e.target.value)}
                  data-testid="dept-subject-override"
                />
              )}
            </div>

            {/* Step 2: Health Systems */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-700">2. Healthcare Systems</Label>
                <span className="text-xs text-slate-500">{deptSendHealthSystems.length} selected of {healthSystemList.length}</span>
              </div>
              <div className="mt-1.5 max-h-44 overflow-y-auto rounded-md border border-slate-200 bg-slate-50/50 p-2 grid grid-cols-2 gap-1">
                {healthSystemList.length === 0 ? (
                  <p className="col-span-2 text-xs text-slate-500 py-3 text-center">No healthcare systems registered yet.</p>
                ) : healthSystemList.map(hs => {
                  const checked = deptSendHealthSystems.includes(hs.id);
                  const contactCount = (hs.contacts || []).length;
                  return (
                    <label key={hs.id} className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer ${checked ? 'bg-emerald-50 ring-1 ring-emerald-300' : 'hover:bg-white'}`} data-testid={`dept-hs-${hs.id}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...deptSendHealthSystems, hs.id]
                            : deptSendHealthSystems.filter(x => x !== hs.id);
                          setDeptSendHealthSystems(next);
                          refreshDeptPreview(next, deptSendDepartments);
                        }}
                      />
                      <span className="truncate flex-1">
                        <span className="font-medium text-slate-800">{hs.name}</span>
                        <span className="ml-1 text-slate-400">({contactCount})</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Step 3: Department filter */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-700">3. Departments</Label>
                <span className="text-xs text-slate-500">{deptSendDepartments.length === 0 ? "All departments" : `${deptSendDepartments.length} selected`}</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  const on = deptSendSegment === "exec_finance";
                  const next = on ? null : "exec_finance";
                  setDeptSendSegment(next);
                  if (!on) setDeptSendDepartments([]);
                  refreshDeptPreview(deptSendHealthSystems, on ? deptSendDepartments : [], next);
                }}
                className={`mt-2 w-full text-left px-3 py-2 rounded-lg border text-[11px] transition-colors ${
                  deptSendSegment === "exec_finance"
                    ? "bg-amber-50 border-amber-400 text-amber-900"
                    : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
                data-testid="segment-exec-finance"
              >
                <span className="font-bold uppercase tracking-wider">
                  {deptSendSegment === "exec_finance" ? "✓ " : "+ "}Executive &amp; Finance (CFO) preset
                </span>
                <span className="block mt-0.5 text-[10.5px] opacity-80">
                  CFOs, finance, administration and executive leadership, plus supply chain
                  directors &amp; VPs. Excludes day-to-day procurement staff. Overrides the
                  department checkboxes below.
                </span>
              </button>
              <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {[
                  "Supply Chain","Facilities Management","Environmental Services (EVS)",
                  "Biomedical Engineering (Bio Med)","Sterile Processing (SPD)","Nursing",
                  "Administration","Finance / Billing","IT / Informatics",
                  "Infection Prevention","Operations","Risk / Compliance",
                  "Executive Leadership","Other"
                ].map(dept => {
                  const checked = deptSendDepartments.includes(dept);
                  return (
                    <label key={dept} className={`flex items-center gap-1.5 px-2 py-1 rounded text-[11px] cursor-pointer border ${checked ? 'bg-emerald-50 border-emerald-300 text-emerald-900 font-medium' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-testid={`dept-${dept.split(' ')[0]}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...deptSendDepartments, dept]
                            : deptSendDepartments.filter(x => x !== dept);
                          setDeptSendDepartments(next);
                          refreshDeptPreview(deptSendHealthSystems, next);
                        }}
                      />
                      {dept}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Live preview */}
            {deptSendHealthSystems.length > 0 && (
              <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-3" data-testid="dept-preview-box">
                {!deptPreview ? (
                  <p className="text-sm text-slate-500">Loading recipient preview...</p>
                ) : deptPreview.total_recipients === 0 ? (
                  <p className="text-sm text-amber-700 font-medium">⚠ No contacts match this filter. Try widening departments or picking different healthcare systems.</p>
                ) : (
                  <div>
                    <p className="text-sm font-bold text-emerald-900">
                      {deptPreview.total_recipients} contact{deptPreview.total_recipients === 1 ? "" : "s"} will receive this email
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3 mt-2 text-xs">
                      <div>
                        <p className="font-semibold text-slate-600 mb-0.5">By department</p>
                        <ul className="space-y-0.5">
                          {Object.entries(deptPreview.by_department).map(([d, n]) => (
                            <li key={d} className="text-slate-700">{d}: <span className="font-medium">{n}</span></li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-600 mb-0.5">By healthcare system</p>
                        <ul className="space-y-0.5">
                          {Object.entries(deptPreview.by_health_system).map(([h, n]) => (
                            <li key={h} className="text-slate-700 truncate">{h}: <span className="font-medium">{n}</span></li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeptSendOpen(false)}>Cancel</Button>
            <Button
              onClick={sendDepartmentCampaign}
              disabled={deptSending || !deptSendTemplate || !deptSendHealthSystems.length || !deptPreview || deptPreview.total_recipients === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="dept-send-btn"
            >
              {deptSending ? "Sending..." : `Send to ${deptPreview?.total_recipients || 0} contact${deptPreview?.total_recipients === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-template Schedule dialog (opens from a Schedule button on each template card) */}
      <ScheduleSendDialog
        open={!!scheduleTemplate}
        onOpenChange={(v) => { if (!v) setScheduleTemplate(null); }}
        template={scheduleTemplate}
        token={token}
        onScheduled={() => {
          toast.success("Open the Scheduler tab to view all upcoming sends");
        }}
      />
    </div>
  );
}

// ===========================
// TELEGRAM BULK INVITE
// ===========================

function TelegramBulkInvite({ token, onSendComplete }) {
  const [selectedGroup, setSelectedGroup] = useState("");
  const [includeConnected, setIncludeConnected] = useState(false);
  const [previewCount, setPreviewCount] = useState(0);
  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fetchPreview = async (group) => {
    if (!group) return;
    setLoadingPreview(true);
    try {
      const res = await fetch(`${API}/admin/telegram-invite/preview?recipient_type=${group}&include_already_connected=${includeConnected}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setPreviewCount(data.total_count); setPreviewRecipients(data.recipients || []); }
    } catch (err) { console.error("Failed to fetch preview:", err); }
    finally { setLoadingPreview(false); }
  };

  useEffect(() => { if (selectedGroup) fetchPreview(selectedGroup); }, [selectedGroup, includeConnected]);

  const handleSendInvites = async () => {
    if (!selectedGroup || previewCount === 0) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/admin/bulk-telegram-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ recipient_type: selectedGroup, include_already_connected: includeConnected })
      });
      const data = await res.json();
      if (res.ok) { toast.success(`Telegram invites sent to ${data.sent_count} users!`); setSelectedGroup(""); setPreviewCount(0); setShowPreview(false); if (onSendComplete) onSendComplete(); }
      else { toast.error(data.detail || "Failed to send invites"); }
    } catch { toast.error("Failed to send Telegram invites"); }
    finally { setSending(false); }
  };

  return (
    <Card className="mb-6 border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-blue-500" />Bulk Telegram Invites</CardTitle>
        <CardDescription>Send Telegram connection invite emails to users who have not connected yet</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="text-slate-700">Select User Group</Label>
            <Select value={selectedGroup} onValueChange={setSelectedGroup}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Choose recipients..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all"><div className="flex items-center gap-2"><Users className="w-4 h-4" />All Users</div></SelectItem>
                <SelectItem value="hospitals"><div className="flex items-center gap-2"><Building2 className="w-4 h-4" />Hospital Users</div></SelectItem>
                <SelectItem value="contractors"><div className="flex items-center gap-2"><Truck className="w-4 h-4" />Contractors</div></SelectItem>
                <SelectItem value="suppliers"><div className="flex items-center gap-2"><Package className="w-4 h-4" />Suppliers</div></SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="include-connected" checked={includeConnected} onChange={(e) => setIncludeConnected(e.target.checked)} className="rounded border-slate-300" />
              <Label htmlFor="include-connected" className="text-sm text-slate-600 cursor-pointer">Include already connected users</Label>
            </div>
            {selectedGroup && <p className="text-sm text-slate-600">{loadingPreview ? <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Loading...</span> : <><span className="font-semibold text-blue-600">{previewCount}</span> users will receive invites</>}</p>}
          </div>
          <div className="flex gap-2">
            {selectedGroup && previewCount > 0 && <Button variant="outline" onClick={() => setShowPreview(true)} disabled={loadingPreview}><Eye className="w-4 h-4 mr-1" />Preview</Button>}
            <Button onClick={handleSendInvites} disabled={!selectedGroup || previewCount === 0 || sending} className="bg-blue-600 hover:bg-blue-700" data-testid="send-telegram-invites-btn">
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Invites</>}
            </Button>
          </div>
        </div>
      </CardContent>
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[70vh] overflow-hidden">
          <DialogHeader><DialogTitle>Preview Recipients ({previewCount})</DialogTitle><DialogDescription>These users will receive Telegram connection invite emails</DialogDescription></DialogHeader>
          <div className="overflow-auto max-h-[50vh]">
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {previewRecipients.slice(0, 50).map((r, idx) => (
                  <TableRow key={idx}><TableCell className="font-medium">{r.name}</TableCell><TableCell className="text-slate-600">{r.email}</TableCell><TableCell><Badge variant="outline" className="text-xs">{r.user_type}</Badge></TableCell><TableCell>{r.telegram_connected ? <Badge className="bg-green-100 text-green-700 text-xs">Connected</Badge> : <Badge className="bg-slate-100 text-slate-600 text-xs">Not Connected</Badge>}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
            {previewRecipients.length > 50 && <p className="text-center text-sm text-slate-500 py-2">Showing 50 of {previewRecipients.length} recipients</p>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
